import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState('');
  const [meta, setMeta] = useState({ email: '', user_type: '' });
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/reset-password/${token}/validate`);
        setValid(Boolean(res.data?.valid));
        if (!res.data?.valid) setReason(res.data?.reason || 'invalid');
        setMeta({ email: res.data?.email || '', user_type: res.data?.user_type || '' });
      } catch (_) {
        setValid(false);
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success('Wachtwoord gewijzigd!');
      setTimeout(() => {
        navigate(meta.user_type === 'klant' ? `/client?email=${encodeURIComponent(meta.email)}` : `/login?email=${encodeURIComponent(meta.email)}`);
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fout bij wijzigen wachtwoord');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20" data-testid="reset-password-card">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-7 h-7 text-[#3da5a5]" />
          <h1 className="text-2xl font-bold text-white">Nieuw wachtwoord instellen</h1>
        </div>

        {validating ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#3da5a5] mx-auto" />
            <p className="text-white/70 text-sm mt-3">Even geduld...</p>
          </div>
        ) : !valid ? (
          <div className="text-center space-y-4 py-4" data-testid="reset-invalid-state">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Link niet meer geldig</h2>
            <p className="text-white/70 text-sm">
              {reason === 'expired'
                ? 'Deze link is verlopen (maximaal 1 uur geldig). Vraag een nieuwe link aan.'
                : 'Deze link is ongeldig of al gebruikt. Vraag een nieuwe link aan.'}
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="reset-request-new-btn">Nieuwe link aanvragen</Button>
            </Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-3 py-4" data-testid="reset-done-state">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Wachtwoord gewijzigd!</h2>
            <p className="text-white/70 text-sm">U wordt doorgestuurd om in te loggen...</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-white/70 text-sm">
              U bent ingelogd als <strong className="text-white">{meta.email}</strong> ({meta.user_type === 'klant' ? 'Klant' : 'Medewerker'}). Kies een nieuw wachtwoord.
            </p>
            <div>
              <Label className="text-white mb-2 block">Nieuw wachtwoord *</Label>
              <Input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                required
                minLength="6"
                className="bg-white/10 border-white/30 text-white"
                data-testid="reset-pwd-input"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Wachtwoord bevestigen *</Label>
              <Input
                type="password"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                required
                minLength="6"
                className="bg-white/10 border-white/30 text-white"
                data-testid="reset-pwd2-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !pwd || !pwd2}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] py-6 text-lg font-semibold"
              data-testid="reset-submit-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Wachtwoord opslaan'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
